<template>
  <div
    class="list-edit-field"
    :class="{ 'is-edit-restricted': !canEditByUserRule }"
  >
    <!-- Campo de fecha editable en listado -->
    <div
      v-if="(field.type === 'date' || field.type === 'datetime') && field.listedit"
      class="editable-container"
    >
      <!-- Modo visualización con lápiz -->
      <span
        v-if="!isEditing"
        class="field-display"
        @click="startEdit"
        :title="'Click to edit'"
      >
        <i class="fas fa-edit text-muted p-1" style="font-size: 0.8em"></i>
        <FieldDate
          :field="field"
          :value="value"
          mode="view"
          :item="item"
          :modelName="''"
          :isGroupModel="false"
        />
      </span>

      <!-- Modo edición - Usando FieldDate de model-definition-core -->
      <div v-else class="list-edit-field-wrapper">
        <FieldDate
          :field="field"
          :value="localValue"
          mode="edit"
          :fieldId="`list-edit-${field.key}-${item._id}`"
          :editingFields="isEditing ? field.key : ''"
          @input="handleFieldDateInput"
          class="list-edit-date-input"
          ref="editInput"
        />
      </div>
    </div>

    <!-- Campo de texto/string editable en listado - Usando FieldText de model-definition-core -->
    <div
      v-else-if="(field.type === 'text' || field.type === 'string' || field.type === 'email' || field.type === 'textarea') && field.listedit"
      class="editable-container"
    >
      <!-- Modo visualización con lápiz -->
      <span
        v-if="!isEditing"
        class="field-display"
        @click="startEdit"
        :title="'Click to edit'"
      >
        <i class="fas fa-edit text-muted p-1" style="font-size: 0.8em"></i>
        <!-- Usar FieldText en modo view para mostrar el valor -->
        <FieldText
          :field="field"
          :value="value"
          mode="view"
          :item="item"
          :modelName="''"
          :isGroupModel="false"
          class="text-value"
        />
        <!-- Nombre editable en el listado: el triángulo de obligatorios vacíos va igual (#473) -->
        <MissingRequiredWarningIcon
          v-if="field.key === 'name' || field.key === 'title'"
          :item="item"
          class="ms-1"
        />
      </span>

      <!-- Modo edición - Usando FieldText de model-definition-core -->
      <div v-else class="list-edit-field-wrapper">
        <FieldText
          :field="field"
          :value="localValue"
          mode="edit"
          :fieldId="`list-edit-${field.key}-${item._id}`"
          :editingFields="isEditing ? field.key : ''"
          @input="handleFieldTextInput"
          class="list-edit-text-input"
          ref="editInput"
        />
      </div>
    </div>

    <!-- Campo numérico editable en listado - Usando FieldNumber de model-definition-core -->
    <div
      v-else-if="field.type === 'number' && field.listedit"
      class="editable-container"
    >
      <!-- Modo visualización con lápiz -->
      <span
        v-if="!isEditing"
        class="field-display"
        @click="startEdit"
        :title="'Click to edit'"
      >
        <i class="fas fa-edit text-muted p-1" style="font-size: 0.8em"></i>
        <!-- Usar FieldNumber en modo view para mostrar el valor -->
        <FieldNumber
          :field="field"
          :value="value"
          mode="view"
          :item="item"
          :modelName="''"
          :isGroupModel="false"
          :lists="lists"
        />
      </span>

      <!-- Modo edición - Usando FieldNumber de model-definition-core -->
      <div v-else class="list-edit-field-wrapper">
        <FieldNumber
          :field="field"
          :value="localValue"
          mode="edit"
          :fieldId="`list-edit-${field.key}-${item._id}`"
          :editingFields="isEditing ? field.key : ''"
          @input="handleFieldNumberInput"
          class="list-edit-number-input"
          ref="editInput"
        />
      </div>
    </div>

    <!-- Campo booleano editable en listado -->
    <div
      v-else-if="field.type === 'boolean' && field.listedit"
      class="editable-container"
    >
      <span
        v-if="!isEditing"
        class="field-display"
        @click="startEdit"
        :title="'Click to edit'"
      >
        <i class="fas fa-edit text-muted p-1" style="font-size: 0.8em"></i>
        <FieldBoolean
          :field="field"
          :value="value"
          mode="view"
          :item="item"
          :modelName="''"
          :isGroupModel="false"
          :showLabel="false"
        />
      </span>
      <div v-else class="list-edit-field-wrapper">
        <FieldBoolean
          :field="field"
          :value="localValue"
          mode="edit"
          :item="item"
          :fieldId="`list-edit-${field.key}-${item._id}`"
          :editingFields="isEditing ? field.key : ''"
          :disableLock="true"
          :modelName="''"
          :isGroupModel="false"
          :showLabel="false"
          @input="handleFieldBooleanInput"
          class="list-edit-boolean-input"
          ref="editInput"
        />
      </div>
    </div>

    <!-- Campo list editable en listado (select en modal) -->
    <div
      v-else-if="field.type === 'list' && field.listedit"
      class="editable-container"
    >
      <span
        class="field-display"
        @click="startEdit"
        :title="'Click to edit'"
      >
        <i class="fas fa-edit text-muted p-1" style="font-size: 0.8em"></i>
        <FieldList
          :field="field"
          :value="value"
          mode="view"
          :item="item"
          :modelName="''"
          :isGroupModel="false"
          :lists="lists"
          :showLabel="false"
        />
      </span>

      <div
        v-if="showListModal"
        class="list-edit-list-modal-root"
        role="presentation"
      >
        <div class="modal-backdrop fade show" @click.self="cancelListModal"></div>
        <div
          class="modal fade show"
          tabindex="-1"
          role="dialog"
          @click.self="cancelListModal"
          @keyup.esc="cancelListModal"
        >
          <div class="modal-dialog modal-md modal-dialog-centered">
            <div class="modal-content" @click.stop>
              <div class="modal-header">
                <h5 class="modal-title">
                  <i class="fas fa-edit me-2 text-secondary" aria-hidden="true"></i>
                  {{ listModalTitle }}
                </h5>
                <button
                  type="button"
                  class="btn-close"
                  :aria-label="$t('Cancel')"
                  @click="cancelListModal"
                ></button>
              </div>
              <div class="modal-body">
                <FieldList
                  :field="field"
                  :value="modalListValue"
                  mode="edit"
                  :fieldId="`list-edit-modal-${field.key}-${item._id}`"
                  :editingFields="field.key"
                  :disableLock="true"
                  :lists="lists"
                  :item="item"
                  :modelName="''"
                  :isGroupModel="false"
                  :showLabel="false"
                  @input="onModalListInput"
                />
              </div>
              <div class="modal-footer">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="cancelListModal"
                >
                  {{ $t("Cancel") }}
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="saveListModal"
                >
                  {{ $t("Save") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Campo file editable en listado (upload en modal) -->
    <div
      v-else-if="field.type === 'file' && field.listedit"
      class="editable-container"
    >
      <span class="field-display field-display-file" @click="onFileDisplayClick">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary list-edit-file-btn"
          :title="$t('Edit') || 'Edit'"
          @click.stop="startEdit"
        >
          <i class="fas fa-edit" aria-hidden="true"></i>
        </button>
        <FieldFile
          :field="field"
          :value="value"
          mode="view"
          :item="item"
          :modelName="''"
          :isGroupModel="false"
          :showLabel="false"
        />
      </span>

      <div
        v-if="showFileModal"
        class="list-edit-list-modal-root"
        role="presentation"
      >
        <div class="modal-backdrop fade show" @click.self="cancelFileModal"></div>
        <div
          class="modal fade show"
          tabindex="-1"
          role="dialog"
          @click.self="cancelFileModal"
          @keyup.esc="cancelFileModal"
        >
          <div class="modal-dialog modal-md modal-dialog-centered">
            <div class="modal-content" @click.stop>
              <div class="modal-header">
                <h5 class="modal-title">
                  <i class="fas fa-edit me-2 text-secondary" aria-hidden="true"></i>
                  {{ listModalTitle }}
                </h5>
                <button
                  type="button"
                  class="btn-close"
                  :aria-label="$t('Cancel')"
                  @click="cancelFileModal"
                ></button>
              </div>
              <div class="modal-body">
                <FieldFile
                  :field="field"
                  :value="modalFileValue"
                  mode="edit"
                  :fieldId="`list-edit-modal-file-${field.key}-${item._id}`"
                  :editingFields="field.key"
                  :disableLock="true"
                  :item="item"
                  :modelName="''"
                  :isGroupModel="false"
                  :showLabel="false"
                  @input="onModalFileInput"
                  @uploading="onModalFileUploading"
                />
              </div>
              <div class="modal-footer">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="cancelFileModal"
                  :disabled="fileModalUploading"
                >
                  {{ $t("Cancel") }}
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="saveFileModal"
                  :disabled="fileModalUploading"
                >
                  <span
                    v-if="fileModalUploading"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                  ></span>
                  {{ $t("Save") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  FieldText,
  FieldNumber,
  FieldDate,
  FieldBoolean,
  FieldList,
  FieldFile,
} from "../model-definition-core";
import { canCurrentUserEditField } from "@/utils/fieldEditableByUser";
import MissingRequiredWarningIcon from "./MissingRequiredWarningIcon.vue";

export default {
  name: "ListEditField",
  components: {
    FieldText,
    FieldNumber,
    FieldDate,
    FieldBoolean,
    FieldList,
    FieldFile,
    MissingRequiredWarningIcon,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Date, Object, Boolean, Array],
      default: null,
    },
    item: {
      type: Object,
      required: true,
    },
    lists: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isEditing: false,
      localValue: null,
      originalValue: null,
      inputTimeout: null,
      showListModal: false,
      modalListValue: null,
      showFileModal: false,
      modalFileValue: null,
      fileModalUploading: false,
    };
  },
  computed: {
    canEditByUserRule() {
      return canCurrentUserEditField(this.field, this.item);
    },
    listModalTitle() {
      const label = this.field && (this.field.label || this.field.key);
      const edited = this.$t("Edit") || "Edit";
      if (!label) return edited;
      const translated =
        typeof label === "string" && this.$te && this.$te(label)
          ? this.$t(label)
          : label;
      return `${edited}: ${translated}`;
    },
  },
  beforeDestroy() {
    if (this.inputTimeout) {
      clearTimeout(this.inputTimeout);
    }
    this.removeEventListeners();
    this.unlockBodyScroll();
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.localValue = newValue;
        this.originalValue = newValue;
      },
    },
  },
  methods: {
    updateLocalValue(newValue) {
      this.localValue = newValue;
    },

    handleFieldTextInput(newValue) {
      this.updateLocalValue(newValue);
    },

    handleFieldNumberInput(newValue) {
      const numValue =
        newValue === "" || newValue === null ? null : parseFloat(newValue);
      const finalValue = isNaN(numValue) ? null : numValue;
      this.updateLocalValue(finalValue);
    },

    handleFieldDateInput(newValue) {
      this.updateLocalValue(newValue);
    },

    handleFieldBooleanInput(newValue) {
      this.updateLocalValue(newValue === true);
    },

    onModalListInput(newValue) {
      this.modalListValue = newValue;
    },

    onModalFileInput(newValue) {
      this.modalFileValue = newValue;
    },

    onModalFileUploading(isUploading) {
      this.fileModalUploading = !!isUploading;
    },

    onFileDisplayClick(event) {
      if (event && event.target && event.target.closest("a")) {
        return;
      }
      this.startEdit();
    },

    lockBodyScroll() {
      if (typeof document === "undefined") return;
      document.body.classList.add("list-edit-list-modal-open");
    },

    unlockBodyScroll() {
      if (typeof document === "undefined") return;
      document.body.classList.remove("list-edit-list-modal-open");
    },

    openListModal() {
      this.modalListValue = this.value;
      this.showListModal = true;
      this.lockBodyScroll();
    },

    cancelListModal() {
      this.showListModal = false;
      this.modalListValue = this.value;
      this.unlockBodyScroll();
    },

    saveListModal() {
      const next = this.modalListValue;
      this.showListModal = false;
      this.unlockBodyScroll();
      if (!this.listValuesEqual(next, this.originalValue)) {
        this.localValue = next;
        this.emitFieldUpdate(next);
        this.originalValue = next;
      }
    },

    openFileModal() {
      this.modalFileValue = this.cloneFileValue(this.value);
      this.fileModalUploading = false;
      this.showFileModal = true;
      this.lockBodyScroll();
    },

    cancelFileModal() {
      if (this.fileModalUploading) return;
      this.showFileModal = false;
      this.modalFileValue = this.cloneFileValue(this.value);
      this.fileModalUploading = false;
      this.unlockBodyScroll();
    },

    saveFileModal() {
      if (this.fileModalUploading) return;
      const next = this.modalFileValue;
      this.showFileModal = false;
      this.unlockBodyScroll();
      if (!this.fileValuesEqual(next, this.originalValue)) {
        this.localValue = next;
        this.emitFieldUpdate(next);
        this.originalValue = next;
      }
    },

    cloneFileValue(v) {
      if (Array.isArray(v)) return [...v];
      return v;
    },

    normalizeFileKey(v) {
      if (v == null || v === "") return "";
      if (typeof v === "string") return v.trim();
      if (typeof v === "object" && v.url) return String(v.url);
      return String(v);
    },

    fileValuesEqual(a, b) {
      if (Array.isArray(a) || Array.isArray(b)) {
        const aa = (Array.isArray(a) ? a : a ? [a] : [])
          .map((x) => this.normalizeFileKey(x))
          .filter(Boolean);
        const bb = (Array.isArray(b) ? b : b ? [b] : [])
          .map((x) => this.normalizeFileKey(x))
          .filter(Boolean);
        if (aa.length !== bb.length) return false;
        return aa.every((k, i) => k === bb[i]);
      }
      return this.normalizeFileKey(a) === this.normalizeFileKey(b);
    },

    listValueId(v) {
      if (v == null || v === "") return "";
      if (Array.isArray(v)) {
        return v
          .map((x) => this.listValueId(x))
          .filter(Boolean)
          .join(",");
      }
      if (typeof v === "object") {
        const id = v._id != null ? v._id : v.id;
        return id != null ? String(id) : "";
      }
      return String(v);
    },

    listValuesEqual(a, b) {
      return this.listValueId(a) === this.listValueId(b);
    },

    emitFieldUpdate(newValue) {
      this.$emit("field-updated", {
        fieldKey: this.field.key,
        itemId: this.item._id,
        value: newValue,
      });
    },

    startEdit() {
      if (!this.canEditByUserRule) return;
      if (this.field.readonly) return;
      if (this.field.type === "list") {
        this.openListModal();
        return;
      }
      if (this.field.type === "file") {
        this.openFileModal();
        return;
      }
      this.isEditing = true;
      this.$nextTick(() => {
        const fieldComponent = this.$refs.editInput;
        if (fieldComponent) {
          const inputElement = fieldComponent.$el?.querySelector(
            "input, textarea"
          );
          if (inputElement) {
            inputElement.focus();
            if (
              typeof inputElement.select === "function" &&
              inputElement.type !== "checkbox"
            ) {
              inputElement.select();
            }
            inputElement.addEventListener("keyup", this.handleKeyUp);
            inputElement.addEventListener("blur", this.handleBlur);
          }
        }
      });
    },

    handleKeyUp(event) {
      if (event.key === "Enter") {
        event.preventDefault();
        this.finishEdit();
      } else if (event.key === "Escape") {
        event.preventDefault();
        this.cancelEdit();
      }
    },

    handleBlur(event) {
      const relatedTarget = event.relatedTarget;
      if (relatedTarget && this.$el.contains(relatedTarget)) {
        return;
      }
      this.finishEdit();
    },

    finishEdit() {
      this.removeEventListeners();
      this.isEditing = false;
      if (this.localValue !== this.originalValue) {
        this.emitFieldUpdate(this.localValue);
        this.originalValue = this.localValue;
      }
    },

    finishNumberEdit() {
      this.removeEventListeners();
      this.isEditing = false;
      const numValue = parseFloat(this.localValue);
      const finalValue = isNaN(numValue) ? null : numValue;
      if (finalValue !== this.originalValue) {
        this.emitFieldUpdate(finalValue);
        this.originalValue = finalValue;
        this.localValue = finalValue;
      }
    },

    cancelEdit() {
      this.removeEventListeners();
      this.isEditing = false;
      this.localValue = this.originalValue;
    },

    removeEventListeners() {
      const fieldComponent = this.$refs.editInput;
      if (fieldComponent) {
        const inputElement = fieldComponent.$el?.querySelector(
          "input, textarea"
        );
        if (inputElement) {
          inputElement.removeEventListener("keyup", this.handleKeyUp);
          inputElement.removeEventListener("blur", this.handleBlur);
        }
      }
    },
  },
};
</script>

<style scoped>
.list-edit-list-modal-root {
  position: fixed;
  inset: 0;
  z-index: 1060;
}

.list-edit-list-modal-root .modal {
  display: block;
}

.list-edit-list-modal-root .modal-body {
  min-height: 80px;
}

.list-edit-list-modal-root .modal-body :deep(.multiselect) {
  z-index: 2;
}

.editable-container {
  position: relative;
  width: 100%;
}

.list-edit-field.is-edit-restricted .field-display {
  cursor: default;
}

.list-edit-field.is-edit-restricted .field-display:hover {
  background-color: transparent;
}

.list-edit-field.is-edit-restricted .fa-edit,
.list-edit-field.is-edit-restricted .list-edit-file-btn {
  display: none;
}

.field-display {
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 2px 4px;
  border-radius: 3px;
  transition: background-color 0.2s;
}

.field-display:hover {
  background-color: #f8f9fa;
}

.field-display-file {
  cursor: pointer;
  gap: 4px;
}

.field-display-file:hover {
  background-color: #f8f9fa;
}

.list-edit-file-btn {
  padding: 0 4px;
  line-height: 1.2;
  flex-shrink: 0;
}

.list-edit-file-btn i {
  font-size: 0.75rem;
  opacity: 1;
}

.field-display:hover i {
  opacity: 1;
}

.field-display i {
  opacity: 0.6;
  transition: opacity 0.2s;
}

.text-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.list-edit-input {
  width: 100%;
  min-width: 120px;
}

.list-edit-input :deep(input) {
  border: 1px solid #007bff;
  border-radius: 3px;
  padding: 2px 4px;
  font-size: 0.9em;
}

.list-edit-text-input {
  width: 100%;
  min-width: 100px;
}

.list-edit-text-input :deep(input),
.list-edit-text-input :deep(textarea) {
  border: 1px solid #007bff !important;
  border-radius: 3px;
  padding: 2px 8px;
  font-size: 0.9em;
  width: 100%;
  min-width: 100px;
}

.list-edit-text-input :deep(input:focus),
.list-edit-text-input :deep(textarea:focus) {
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  outline: none;
}

.list-edit-number-input {
  border: 1px solid #007bff !important;
  border-radius: 3px;
  padding: 2px 8px;
  font-size: 0.9em;
  width: 100%;
  min-width: 80px;
  max-width: 120px;
  text-align: right;
}

.list-edit-number-input:focus {
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  outline: none;
}

.list-edit-date-input {
  width: 100%;
  min-width: 140px;
}

.list-edit-date-input :deep(input) {
  border: 1px solid #007bff !important;
  border-radius: 3px;
  padding: 2px 8px;
  font-size: 0.9em;
  width: 100%;
  min-width: 140px;
}

.list-edit-date-input :deep(input:focus) {
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  outline: none;
}

.list-edit-boolean-input {
  width: 100%;
  min-width: 72px;
}

.list-edit-boolean-input :deep(.form-check) {
  margin-bottom: 0;
  padding-left: 0;
}

.list-edit-boolean-input :deep(.form-check-input) {
  margin-left: 0;
  float: none;
}

.list-edit-number-input::-webkit-outer-spin-button,
.list-edit-number-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.list-edit-number-input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>

<style>
body.list-edit-list-modal-open {
  overflow: hidden;
}
</style>
